/**
 * Collapsed "cutoff approaching" summary row. The backend emits one root event
 * per booking whose cargo cutoff is inside the alert window, which floods the
 * feed with near-identical rows. This renders a single summary pill and, when
 * expanded, a drill-down of the affected bookings grouped by days-until-cutoff
 * (most urgent first), each with its scope chips and a deep link to the booking.
 *
 * Grouping upstream (`groupFeedEventsForDisplay`) guarantees `events` is a
 * non-empty, newest-first list of `cutoff_approaching` events.
 */
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronDown, Sparkles, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useFeedOptional } from "@/context/FeedContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { usePortNames } from "@/hooks/usePortNames";
import { useLongPress } from "@/hooks/useLongPress";
import { useAskKai } from "@/hooks/useAskKai";
import { buildAskKaiFeedEventPrompt } from "@/features/kai/kaiFeedEventContext";
import { extractLaneLocodes } from "@/lib/formatPort";
import { SensitiveText, SensitiveInlineText } from "@/components/common/Sensitive";
import type { FeedEvent } from "@/types/feed";
import { FeedEventCardMeta, FeedReviewedMark } from "./FeedEventCardMeta";
import {
  formatRelativeTime,
  FEED_SEVERITY_BORDER,
} from "./feedVisuals";
import { buildScopeChips } from "./FeedEventRow";
import { bookingHref } from "./feedLinks";
import {
  cutoffBookingId,
  cutoffDate,
  groupCutoffEventsByDays,
} from "./feedCutoffGroups";
import { FeedRowSlot } from "./FeedRowSlot";
import { useFeedRowReview } from "./useFeedRowReview";
import { FeedBookingDrillPartyLine } from "./FeedBookingDrillParties";

interface FeedCutoffGroupRowProps {
  events: FeedEvent[];
  index?: number;
  entering?: boolean;
  /** Override the default index × 70ms stagger (feed page load). */
  enterDelayMs?: number;
  /** Fallback dismiss when rendered outside `FeedProvider` (e.g. booking panel). */
  onDismiss?: (eventIds: string[]) => void | Promise<void>;
  /** 10s live-arrival border glow after the viewer is positioned on this row. */
  highlighted?: boolean;
}

/** Label for a days-until bucket subheader. */
function bucketLabel(daysUntil: number | null): string {
  if (daysUntil === null) return "Cutoff date unknown";
  if (daysUntil <= 0) return "Cutoff today";
  return `In ${daysUntil} day${daysUntil === 1 ? "" : "s"}`;
}

/** One booking row inside an expanded days-until bucket. */
function CutoffBookingRow({
  event,
  showLocodes,
  portNames,
}: {
  event: FeedEvent;
  showLocodes: boolean;
  portNames: ReadonlyMap<string, string>;
}) {
  const sourceId = cutoffBookingId(event);
  const chips = buildScopeChips(event, showLocodes, portNames);
  const cutoff = cutoffDate(event);
  const scope = event.entity_scope ?? {};

  return (
    <div className="rounded-lg bg-white/5 px-2.5 py-2">
      <div className="flex items-start justify-between gap-2">
        {sourceId ? (
          <Link
            to={bookingHref(sourceId)}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
          >
            #<SensitiveInlineText text={sourceId} />
            <ArrowUpRight className="size-2.5" />
          </Link>
        ) : (
          <span className="text-[11px] text-muted-foreground">Booking</span>
        )}
        {cutoff && (
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
            Cutoff {cutoff}
          </span>
        )}
      </div>
      <FeedBookingDrillPartyLine
        variant="summary"
        omitAgent
        agent={scope.agent}
        consignee={scope.consignee}
      />
      {chips.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground"
            >
              {chip.label} ·{" "}
              {chip.mask ? <SensitiveText value={chip.value} /> : chip.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function FeedCutoffGroupRow({
  events,
  index = 0,
  entering = false,
  enterDelayMs,
  onDismiss,
  highlighted = false,
}: FeedCutoffGroupRowProps) {
  const feed = useFeedOptional();
  const { preferences } = useUserPreferences();
  const rowRef = useRef<HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [detailMounted, setDetailMounted] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const { removing, startReview } = useFeedRowReview();

  const head = events[0];
  const allReviewed = events.every((event) => event.is_reviewed === true);
  const canDismiss = (!!feed || !!onDismiss) && !allReviewed;

  const buckets = useMemo(() => groupCutoffEventsByDays(events), [events]);
  const laneLocodes = useMemo(
    () => extractLaneLocodes(...events.map((e) => e.entity_scope?.lane_key)),
    [events],
  );
  const laneNames = usePortNames(laneLocodes);

  const count = events.length;
  const knownDays = buckets
    .map((b) => b.daysUntil)
    .filter((d): d is number => d !== null);
  const minDays = knownDays.length ? Math.min(...knownDays) : null;
  const maxDays = knownDays.length ? Math.max(...knownDays) : null;

  const summary =
    minDays === null
      ? "Cargo cutoff approaching"
      : minDays === maxDays
        ? `Cargo cutoff in ${minDays} day${minDays === 1 ? "" : "s"}`
        : `Cargo cutoff within ${maxDays} days`;

  const askKai = useAskKai();

  const openActions = () => {
    if (removing) return;
    setActionsOpen(true);
  };
  const longPress = useLongPress(openActions);

  const handleAskKai = () => {
    setActionsOpen(false);
    askKai(buildAskKaiFeedEventPrompt(head), {
      feedEvent: head,
    });
  };

  useEffect(() => {
    if (!expanded) {
      setDetailOpen(false);
      return;
    }
    setDetailMounted(true);
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setDetailOpen(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [expanded]);

  useEffect(() => {
    if (!actionsOpen) return;
    const close = (pointerEvent: PointerEvent) => {
      if (rowRef.current?.contains(pointerEvent.target as Node)) return;
      setActionsOpen(false);
    };
    const id = window.setTimeout(() => {
      document.addEventListener("pointerdown", close);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", close);
    };
  }, [actionsOpen]);

  const rowStyle: CSSProperties | undefined =
    entering || removing
      ? {
          animationDelay: removing
            ? "0ms"
            : `${enterDelayMs ?? index * 70}ms`,
        }
      : undefined;

  const handleReview = () => {
    if (!canDismiss || removing) return;
    setActionsOpen(false);
    setExpanded(false);
    startReview(async () => {
      const ids = events.map((e) => e.id);
      if (onDismiss) await onDismiss(ids);
      else if (feed) await feed.reviewEvents(ids);
    });
  };

  const statusLabel = `${count} booking${count === 1 ? "" : "s"}`;

  return (
    <FeedRowSlot exiting={removing}>
    <article
      ref={rowRef}
      data-feed-event-id={head.id}
      data-feed-event-ids={events.map((event) => event.id).join(" ")}
      className={cn(
        "rail-notification-pill glass-pill clay feed-event-row flex w-full min-w-0 max-w-full flex-col overflow-hidden px-3.5 py-2.5",
        entering && !removing && "rail-notification-pill--enter",
        removing && "rail-notification-pill--review-exit",
        expanded && "rail-notification-pill--expanded min-h-0",
        actionsOpen && "feed-event-row--actions-open",
        highlighted && "feed-event-row--live-focus",
        FEED_SEVERITY_BORDER[head.severity],
      )}
      style={rowStyle}
      onContextMenu={(e) => {
        e.preventDefault();
        openActions();
      }}
      onPointerDown={longPress.onPointerDown}
      onPointerMove={longPress.onPointerMove}
      onPointerUp={longPress.onPointerUp}
      onPointerLeave={longPress.onPointerLeave}
      onPointerCancel={longPress.onPointerCancel}
      onClickCapture={longPress.onClickCapture}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="box-border grid min-h-11 w-full min-w-0 max-w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1.5 text-left sm:gap-x-3"
      >
        <div className="col-start-1 row-start-1 min-w-0 overflow-hidden">
          <FeedEventCardMeta
            typeLabel="Cutoff approaching"
            severity={head.severity}
            horizon={head.time_horizon}
            statusLabel={statusLabel}
            className="mb-1"
          />
          <p className="truncate text-sm font-medium leading-tight">
            Cargo cutoff — {count} booking{count === 1 ? "" : "s"}
          </p>
        </div>

        <span className="col-start-2 row-start-1 flex shrink-0 items-start gap-1 self-start pt-0.5">
          <span className="flex flex-col items-end">
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {formatRelativeTime(head.created_at)}
            </span>
            {allReviewed && <FeedReviewedMark className="mt-0.5" />}
          </span>
          <ChevronDown
            className={cn(
              "size-3.5 text-muted-foreground transition-transform duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
              expanded && "rotate-180",
            )}
          />
        </span>

        <div className="col-span-2 row-start-2 min-w-0 overflow-hidden">
          <p className="truncate text-xs text-muted-foreground">{summary}</p>
        </div>
      </button>

      {detailMounted && (
        <div
          className={cn(
            "feed-row-detail min-h-0 min-w-0",
            detailOpen && "feed-row-detail--open",
          )}
          onTransitionEnd={(e) => {
            if (e.propertyName !== "grid-template-rows" || detailOpen) return;
            setDetailMounted(false);
          }}
        >
          <div className="feed-row-detail__inner">
            <div className="feed-row-detail-body feed-row-detail-body--loose">
              <p className="feed-row-detail-body__heading">
                {count} booking{count === 1 ? "" : "s"} by cutoff
              </p>
              {buckets.map((bucket) => (
                <div
                  key={bucket.daysUntil ?? "unknown"}
                  className="flex flex-col gap-1.5"
                >
                  <p className="px-0.5 text-[10px] font-medium text-muted-foreground/80">
                    {bucketLabel(bucket.daysUntil)} · {bucket.events.length}{" "}
                    booking{bucket.events.length === 1 ? "" : "s"}
                  </p>
                  {bucket.events.map((event) => (
                    <CutoffBookingRow
                      key={event.id}
                      event={event}
                      showLocodes={preferences.showLocodes}
                      portNames={laneNames}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {actionsOpen && (
        <div
          className="feed-row-actions"
          role="toolbar"
          aria-label="Feed row actions"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="feed-row-actions__kai"
            onClick={handleAskKai}
          >
            <Sparkles className="size-3.5" aria-hidden />
            Ask Kai
          </button>
          {canDismiss && (
            <button
              type="button"
              className="feed-row-actions__remove"
              onClick={handleReview}
            >
              <Check className="size-3.5" aria-hidden />
              Mark as reviewed
            </button>
          )}
          <button
            type="button"
            className="feed-row-actions__cancel"
            aria-label="Cancel"
            onClick={() => setActionsOpen(false)}
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}
    </article>
    </FeedRowSlot>
  );
}
